import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Paper,
  Stack,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RouteIcon from '@mui/icons-material/Route';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AgendaVisita } from '../hooks/useAgenda';

// AIDEV-NOTE: Mapeamento de status da agenda para cor do Chip
const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  planejada: 'default',
  em_andamento: 'info',
  concluida: 'success',
  cancelada: 'error'
};

const TIPO_LABEL: Record<string, string> = {
  prospeccao: 'Prospecao',
  manutencao: 'Manutencao',
  cobranca: 'Cobranca',
  entrega: 'Entrega',
  outro: 'Outro'
};

interface AgendaPanelProps {
  visitas: AgendaVisita[];
  onSelectVisita?: (v: AgendaVisita) => void;
  onAddVisita?: () => void;
  onOptimizeRoute?: () => void;
  onReorder?: (reordered: AgendaVisita[]) => void;
  onCheckin?: (visita: AgendaVisita) => void;
  onCheckout?: (visita: AgendaVisita) => void;
  checkinPendingId?: string | null;
  checkoutPendingId?: string | null;
  totalDistance?: string;
  totalDuration?: string;
}

// AIDEV-NOTE: Componente sortable individual — usa @dnd-kit/sortable para drag-and-drop
function AgendaVisitaItem({
  visita,
  onSelect,
  onCheckin,
  onCheckout,
  isCheckinPending,
  isCheckoutPending
}: {
  visita: AgendaVisita;
  onSelect?: (v: AgendaVisita) => void;
  onCheckin?: (v: AgendaVisita) => void;
  onCheckout?: (v: AgendaVisita) => void;
  isCheckinPending?: boolean;
  isCheckoutPending?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: visita.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const horario = visita.data_planejada
    ? new Date(visita.data_planejada).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    : '--:--';

  const duracaoMinutos = visita.visitas?.duracao_minutos;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      onClick={() => onSelect?.(visita)}
    >
      <Box display="flex" alignItems="flex-start" gap={1}>
        <Box
          {...attributes}
          {...listeners}
          sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', pt: 0.25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <DragIndicatorIcon fontSize="small" color="action" />
        </Box>

        <Box flex={1} minWidth={0}>
          {/* Nome + número de ordem */}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="body2" fontWeight={600} noWrap flex={1}>
              {visita.cliente_nome ?? `Cliente ${visita.cliente_id}`}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600} flexShrink={0}>
              #{visita.ordem_dia}
            </Typography>
          </Box>

          {/* Telefone */}
          {visita.cliente_telefone && (
            <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
              <PhoneIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {visita.cliente_telefone}
              </Typography>
            </Box>
          )}

          {/* Endereço / Cidade */}
          {(visita.cliente_endereco || visita.cliente_cidade) && (
            <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
              <LocationOnIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {[visita.cliente_endereco, visita.cliente_cidade].filter(Boolean).join(', ')}
              </Typography>
            </Box>
          )}

          {/* Horário, tipo, status e duração */}
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              {horario}
            </Typography>
            <Chip
              label={TIPO_LABEL[visita.tipo_visita] ?? visita.tipo_visita}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            <Chip
              label={visita.status}
              size="small"
              color={STATUS_COLOR[visita.status] ?? 'default'}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {duracaoMinutos != null && (
              <Box display="flex" alignItems="center" gap={0.25}>
                <AccessTimeIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {duracaoMinutos} min
                </Typography>
              </Box>
            )}
          </Box>

          {/* Botões de check-in / check-out */}
          {(visita.status === 'planejada' || visita.status === 'em_andamento') && (
            <Box display="flex" gap={1} mt={1} onClick={(e) => e.stopPropagation()}>
              {visita.status === 'planejada' && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={
                    isCheckinPending ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <LoginIcon />
                    )
                  }
                  disabled={isCheckinPending}
                  onClick={() => onCheckin?.(visita)}
                  sx={{ fontSize: '0.7rem', py: 0.25 }}
                >
                  Check-in
                </Button>
              )}
              {visita.status === 'em_andamento' && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={
                    isCheckoutPending ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <LogoutIcon />
                    )
                  }
                  disabled={isCheckoutPending}
                  onClick={() => onCheckout?.(visita)}
                  sx={{ fontSize: '0.7rem', py: 0.25 }}
                >
                  Check-out
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export function AgendaPanel({
  visitas,
  onSelectVisita,
  onAddVisita,
  onOptimizeRoute,
  onReorder,
  onCheckin,
  onCheckout,
  checkinPendingId,
  checkoutPendingId,
  totalDistance,
  totalDuration
}: AgendaPanelProps) {
  const [items, setItems] = useState<AgendaVisita[]>(visitas);

  // Sincroniza itens quando prop muda
  useMemo(() => {
    setItems(visitas);
  }, [visitas]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  const sortedIds = useMemo(() => items.map((v) => v.id), [items]);

  // AIDEV-NOTE: handleDragEnd faz reordenamento otimista e notifica o pai para persistir
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((v) => v.id === active.id);
      const newIndex = prev.findIndex((v) => v.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex).map((v, i) => ({
        ...v,
        ordem_dia: i + 1
      }));
      onReorder?.(reordered);
      return reordered;
    });
  };

  const hoje = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit'
  });

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Agenda de Hoje
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hoje}
          </Typography>
        </Box>
        <IconButton color="primary" onClick={onAddVisita} aria-label="adicionar visita">
          <AddIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Lista com drag-and-drop */}
      <Box flex={1} overflow="auto" p={1}>
        {items.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              Nenhuma visita planejada para hoje.
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
              <Stack spacing={1}>
                {items.map((v) => (
                  <AgendaVisitaItem
                    key={v.id}
                    visita={v}
                    onSelect={onSelectVisita}
                    onCheckin={onCheckin}
                    onCheckout={onCheckout}
                    isCheckinPending={checkinPendingId === v.id}
                    isCheckoutPending={checkoutPendingId === v.id}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        )}
      </Box>

      <Divider />

      {/* Footer */}
      <Box p={2} pt={1}>
        {(totalDistance || totalDuration) && (
          <Box display="flex" justifyContent="space-between" mb={1}>
            {totalDistance && (
              <Typography variant="caption" color="text.secondary">
                Distancia: {totalDistance}
              </Typography>
            )}
            {totalDuration && (
              <Typography variant="caption" color="text.secondary">
                Tempo: {totalDuration}
              </Typography>
            )}
          </Box>
        )}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<RouteIcon />}
          onClick={onOptimizeRoute}
          size="small"
        >
          Otimizar Rota
        </Button>
      </Box>
    </Box>
  );
}
